/* The exercise card — KANON `Cwiczenia` 17.09.2026, variant e · Teatr v2. The scrim, the stage, the
 * 1240 x 860 panel and its bar.
 *
 * The rail, the hero, the strip, the materials and the walk each carry their own sheet; this one is
 * the BOX and the bar, and nothing else.
 *
 * Every text leaf states its line-height. Every colour, space, radius and duration is a token — the
 * scrim's own ink included, which lives in `tokens/colors.css` beside every other scrim in this app
 * because a photograph and a dimmed screenshot are the same in both themes. */

/* ── The scrim ──────────────────────────────────────────────────────────────────────────────── */

/* Neutral ink plus the app's one blur token. Not glass: a glass surface is a translucent tinted
 * panel you can see the page through, and this is a scrim with a blur under it — the same
 * construction `BuilderDayModal` already ships, and the one the owner confirmed on 13 September
 * 2026 is outside the ban. */
.scrim {
  position: fixed;
  inset: var(--space-0);
  z-index: 18;
  background: var(--scrim-theatre);
  backdrop-filter: var(--blur-scrim);
}

/* ── The stage ──────────────────────────────────────────────────────────────────────────────── */

/* The POPUP itself, and it is the whole viewport rather than the card. The canon's prev / next
 * squares stand outside the panel, and a modal Base UI dialog takes everything outside its own
 * subtree out of the tab order — so the walk has to be INSIDE the popup, and the popup therefore
 * has to be bigger than the card.
 *
 * `pointer-events: none` here and `auto` on the two things inside, so a press on the empty space
 * reaches the backdrop and still dismisses. */
.stage {
  position: fixed;
  inset: var(--space-0);
  z-index: 20;
  display: grid;
  place-items: center;
  pointer-events: none;
}

.stage:focus-visible {
  outline: none;
}

/* ── The panel ──────────────────────────────────────────────────────────────────────────────── */

/* The canon's 1240 x 860, bounded by the viewport it stands in. The width leaves
 * `--w-ex-theatre-gutter` either side, which is exactly what the walk occupies at the reference
 * viewport — so a narrow window shrinks the card rather than pushing the squares off the screen.
 *
 * `min-height: 0` and `overflow: hidden`: the two columns inside scroll within themselves, and the
 * panel is the box they are bounded by. */
.panel {
  display: flex;
  flex-direction: column;
  width: min(var(--w-ex-theatre), calc(100vw - 2 * var(--w-ex-theatre-gutter)));
  height: min(var(--h-ex-theatre), calc(100dvh - 2 * var(--space-6)));
  min-height: 0;
  overflow: hidden;
  pointer-events: auto;
  background: var(--bg-card);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-md);

  /* Neutral and downward. The app's modal elevation, never a coloured blur. */
  box-shadow: var(--shadow-modal);

  /* `body` sets `--lh-relaxed` app-wide, and the forty-odd micro-labels in this card inheriting
   * prose leading is what pushed the builder's library rail 3.5px down. Carried across for the same
   * reason `DayPanelHost` carries it. */
  line-height: var(--lh-auto);
}

/* ── The bar ────────────────────────────────────────────────────────────────────────────────── */

.bar {
  display: flex;
  flex: none;
  gap: var(--space-10px);
  align-items: center;
  height: var(--h-ex-theatre-bar);
  padding: var(--space-0) var(--space-4) var(--space-0) var(--space-5);
  border-bottom: var(--border-hairline) solid var(--border);
}

/* The type as a DOT and its WORD, in the type's own hue — the canon's own `● SIŁOWE`, and the ONE
 * hue on this surface. The table's TYP column draws a glyph on a wash square instead because it has
 * 56px and no room for a word; the bar has the room and prints it, which is also why nothing here
 * needs a visually hidden name. */
.typeMark {
  display: inline-flex;
  flex: none;
  gap: var(--space-6px);
  align-items: center;
  font-size: var(--fs-label);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-auto);
  white-space: nowrap;
  color: var(--type-ink);
}

.typeGlyph {
  display: block;
  flex: none;
  width: 13px;
  height: 13px;
  color: var(--type-ink);
}

.name {
  flex: 0 1 auto;
  min-width: 0;
  margin: var(--space-0);
  overflow: hidden;
  font-size: var(--fs-h3);
  font-weight: var(--fw-extrabold);
  line-height: var(--lh-display);
  letter-spacing: var(--ls-name);
  color: var(--text);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.badge {
  display: inline-flex;
  flex: none;
  align-items: center;
  height: 18px;
  padding: var(--space-0) var(--space-7px);
  font-size: var(--fs-badge);
  font-weight: var(--fw-extrabold);
  line-height: var(--lh-auto);
  letter-spacing: var(--ls-cat);
  white-space: nowrap;
  color: var(--text);
  background: var(--bg-chip);
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--radius-xs);
}

.spacer {
  flex: 1;
}

/* Board D: „Do uzupełnienia" beside the name, and „Sprawdzone…" once the trainer takes the mark
 * off. Neutral words; the tick is ink, never a tinted pill. */
.review,
.checked {
  display: inline-flex;
  flex: none;
  gap: var(--space-6px);
  align-items: center;
  font-size: var(--fs-note);
  font-weight: var(--fw-bold);
  color: var(--text-2);
  white-space: nowrap;
}

.checkedGlyph {
  width: 14px;
  height: 14px;
  color: var(--text);
}

/* GEOMETRY for `shared/ui/ghostButton` — the treatment is that file's, and nothing is declared on
 * both sides. Every control in this bar is a GHOST: the accent is a scalpel, and neither reading a
 * card nor closing it is the screen's one real action. */
.barButton {
  gap: var(--space-6px);
  height: var(--h-detail-btn);
  padding: var(--space-0) var(--space-3);
  font-size: var(--fs-note);
  font-weight: var(--fw-bold);
  background: var(--bg-card);
  border-radius: var(--radius-8px);
}

.barSquare {
  justify-content: center;
  width: var(--h-detail-btn);
  height: var(--h-detail-btn);
  padding: var(--space-0);
  background: var(--bg-card);
  border-radius: var(--radius-8px);
}

.barGlyph {
  display: block;
  width: 17px;
  height: 17px;
}

.itemGlyph {
  display: block;
  flex: none;
  width: 15px;
  height: 15px;
}

/* ── The body ───────────────────────────────────────────────────────────────────────────────── */

/* 1.4fr / 1fr, the canon's own split. `minmax(0, …)` and not a bare fraction: a grid track's default
 * minimum is its content, so one long instruction would push the rail past the panel's right edge
 * instead of wrapping inside it. */
.body {
  display: grid;
  flex: 1;
  grid-template-columns: minmax(0, 1.4fr) minmax(0, 1fr);
  gap: var(--space-7);
  min-height: 0;
  padding: var(--space-6);
}

/* The hero and the strip under it. The hero is `flex: 1` against this column, the strip is
 * `flex: none` over its own reserve, and the column itself scrolls — so on a short window the two
 * are reachable rather than clipped. */
.left {
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
}

/* The heading takes focus after a save opens this card (`TheatreBar`), so it wears the app's ink
 * ring rather than the browser's. */
.name:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}
